import { LLMOutputMatcher } from '@llm-ui/react';

/**
 * Matcher for complete code blocks
 * This finds code blocks with triple backticks
 */
export const fullCodeBlockMatcher = (): LLMOutputMatcher => {
  return (text) => {
    // This regex matches:
    // 1. Opening fence with optional language identifier
    // 2. Any content (including newlines)
    // 3. Closing fence
    const regex = /(```[\w]*\n?)([\s\S]*?)(```\s*$|```\s*\n)/m;
    const match = regex.exec(text);

    if (match) {
      const openingFence = match[1];
      const codeContent = match[2];
      const closingFence = match[3];
      const fullBlock = openingFence + codeContent + closingFence;

      return {
        startIndex: match.index,
        endIndex: match.index + fullBlock.length,
        outputRaw: fullBlock,
      };
    }

    return undefined;
  };
};

/**
 * Matcher for partial code blocks
 * Finds code blocks that start but don't end
 */
export const partialCodeBlockMatcher = (): LLMOutputMatcher => {
  return (text) => {
    // First check if we have a complete match
    const completeMatch = fullCodeBlockMatcher()(text);
    if (completeMatch) {
      return undefined;
    }

    // Find the last occurrence of an opening code fence that isn't followed by a closing fence
    const regex = /(```[\w]*\n?)([\s\S]*)$/;
    const match = regex.exec(text);

    if (match) {
      const openingFence = match[1];
      const content = match[2];

      // Make sure this isn't accidentally matching a full code block
      if (!content.includes('```')) {
        return {
          startIndex: match.index,
          endIndex: text.length,
          outputRaw: openingFence + content,
        };
      }
    }

    return undefined;
  };
};

/**
 * Extract code and language from a code block string
 * Extracts content between triple backticks
 */
export function extractCode(codeBlock: string): {
  code: string;
  language: string;
} {
  let code = '';
  let language = '';

  // Extract the language (if specified)
  const langMatch = codeBlock.match(/```(\w*)/);
  if (langMatch && langMatch[1]) {
    language = langMatch[1];
  }

  // Extraction of code content
  const contentMatch = codeBlock.match(/```[\w]*\n?([\s\S]*?)```\s*$/);
  if (contentMatch && contentMatch[1]) {
    code = contentMatch[1].trim();
  } else {
    // Fallback for partial blocks or other edge cases
    const parts = codeBlock.split(/```[\w]*\n?/);
    if (parts.length > 1) {
      // Get everything after the first fence but remove trailing backticks if present
      code = parts[1].replace(/```\s*$/, '').trim();
    }
  }

  return { code, language };
}
